import { Cancel01Icon, Download01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { isInstallable, isInstalled, showInstallPrompt } from '@/utils/pwa-install-prompt';

export function PWAInstallButton() {
  const { t } = useTranslation();
  const [installable, setInstallable] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if already installed
    setInstalled(isInstalled());

    // Listen for install prompt availability
    const handleInstallAvailable = () => {
      setInstallable(true);
      setShowPrompt(true);
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);

    // Check initial state
    if (isInstallable()) {
      setInstallable(true);
      setShowPrompt(true);
    }

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
    };
  }, []);

  const handleInstall = async () => {
    const accepted = await showInstallPrompt();
    if (accepted) {
      toast.success(t('pwa.installStarted'));
      setShowPrompt(false);
      setInstallable(false);
    } else {
      toast.info(t('pwa.installCancelled'));
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Store dismissal in localStorage to avoid showing again for this session
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if already installed
  if (installed) {
    return null;
  }

  // Don't show if user dismissed it in this session
  if (!showPrompt || localStorage.getItem('pwa-install-dismissed') === 'true') {
    return null;
  }

  // Don't show if not installable
  if (!installable) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-background border rounded-lg shadow-lg p-4 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm mb-1 flex items-center justify-between">
              {t('pwa.installOpexMES')}
              <Button size="sm" variant="ghost" onClick={handleDismiss}>
                <HugeiconsIcon icon={Cancel01Icon} className="h-4 w-4" />
              </Button>
            </h3>
            <p className="text-xs text-muted-foreground mb-3">{t('pwa.installDescription')}</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleInstall} className="flex-1">
                <HugeiconsIcon icon={Download01Icon} className="h-4 w-4 mr-2" />
                {t('pwa.install')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
